import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import { Card, CardContent } from './ui/card'
import { Popover, PopoverContent, PopoverTrigger, PopoverClose } from './ui/popover'
import { AppIcon } from './ui/icon'
import { mdiViewColumn, mdiAccount, mdiArrowDown, mdiArrowUp, mdiArrowUpDown, mdiCalendar, mdiChartGantt, mdiCheck, mdiClose, mdiFilterVariant, mdiMagnify, mdiPlus, mdiShieldAlertOutline } from '@mdi/js'
import { PIIBadge } from './PIIBadge'
import {
  useContracts,
  useContractTypes
} from '../hooks/useQueries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useLocale } from '../contexts/LocaleContext'
import { useLiveSubscription } from '../contexts/LiveEventsContext'
import { useContractQualityIssues } from '../hooks/useContractQualityIssues'
import { useTicketSubtypes, subtypeLabel } from '../hooks/useTicketSubtypes'
import { Can } from '../contexts/PermissionsContext'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'
import { CONTRACT_STATUS_OPTIONS } from '../lib/contract-status'
import {
  CONTRACT_COLUMNS,
  clearContractColumns,
  defaultContractColumns,
  loadContractColumns,
  saveContractColumns,
  type ContractColumnId,
} from '../lib/contract-columns'

// Number of party chips shown inline before collapsing the rest into a "+N" chip.
const MAX_PARTY_CHIPS = 2
// Number of asset badges shown inline before collapsing the rest into a "+N" badge.
const MAX_ASSET_BADGES = 1
// Contracts whose termination (denunciation) deadline falls within this many days
// are highlighted amber — the tacit-renewal notice window is closing.
const TERMINATION_WARNING_DAYS = 60

interface ContractFilters {
  search?: string
  contractTypeUid?: string
  status?: string
  assetUid?: string
  // 'nonconform' keeps only rows carrying open contract-quality tickets
  // (domain `contract`, spec 2026-07-31-01). Applied client-side on the page.
  conformity?: string
  // 'framework' keeps only framework contracts (contrats cadres), 'application'
  // keeps everything that is not one (spec 2026-08-01-02 §4). Server-side.
  scope?: string
  // Tri-state PII filter (spec 2026-08-13-02 §6). Server-side, expanded onto
  // the contract's pii:* tokens.
  pii?: 'true' | 'false' | 'unknown'
  // 'true' keeps contracts with a natural-person / party-group party, 'false'
  // keeps the others. Server-side EXISTS, kept orthogonal to `pii`.
  individual?: string
  // The notice window the sidebar badge opens (spec 2026-10-27-23):
  // YYYY-MM-DD bounds of termination_date, contracts in force only.
  terminationFrom?: string
  terminationBefore?: string
}

// Statuses render as calm outline badges with a colored dot — a filled
// primary/destructive badge reads as a clickable button in a table row.
// Shared with the detail page's status select via `lib/contract-status.ts`.
const STATUS_OPTIONS = CONTRACT_STATUS_OPTIONS

export function ContractsPage() {
  const { org } = useParams({ strict: false })
  const navigate = useNavigate()
  const { t } = useTranslation('contracts')
  const { locale } = useLocale()

  // Read initial filter state from URL
  const getUrlParams = useCallback(() => {
    const sp = new URLSearchParams(window.location.search)
    return {
      search: sp.get('q') || undefined,
      contractTypeUid: sp.get('contract_type_uid') || undefined,
      status: sp.get('status') || undefined,
      assetUid: sp.get('asset_uid') || undefined,
      conformity: sp.get('conformity') || undefined,
      scope: sp.get('scope') || undefined,
      pii: (['true', 'false', 'unknown'].includes(sp.get('pii') || '')
        ? sp.get('pii')
        : undefined) as ContractFilters['pii'],
      individual: sp.get('individual') || undefined,
      terminationFrom: sp.get('termination_from') || undefined,
      terminationBefore: sp.get('termination_before') || undefined,
    } as ContractFilters
  }, [])

  const getUrlPage = useCallback(() => {
    const sp = new URLSearchParams(window.location.search)
    return parseInt(sp.get('page') || '1', 10)
  }, [])

  // Excel-style column sort — server-side so it orders across all pages.
  type SortField = 'name' | 'start_date'
  const getUrlSort = useCallback((): { by: SortField; dir: 'asc' | 'desc' } | null => {
    const sp = new URLSearchParams(window.location.search)
    const by = sp.get('sort_by')
    if (by !== 'name' && by !== 'start_date') return null
    return { by, dir: sp.get('sort_dir') === 'asc' ? 'asc' : 'desc' }
  }, [])

  const [filters, setFilters] = useState<ContractFilters>(getUrlParams)
  const [page, setPage] = useState(getUrlPage)
  const [sort, setSort] = useState<{ by: SortField; dir: 'asc' | 'desc' } | null>(getUrlSort)
  const pageSize = 20

  // Visible columns, stored per browser. An active filter on a hidden column
  // stays applied: filters live in `filters`, not in the column state.
  const [columns, setColumns] = useState<ContractColumnId[]>(loadContractColumns)
  const isCol = (id: ContractColumnId) => columns.includes(id)
  const toggleColumn = (id: ContractColumnId) => {
    setColumns((prev) => {
      const next = prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
      saveContractColumns(next)
      return next
    })
  }
  const resetColumns = () => {
    clearContractColumns()
    setColumns(defaultContractColumns())
  }
  const visibleColumnDefs = CONTRACT_COLUMNS.filter((c) => columns.includes(c.id))
  const widthTotal = visibleColumnDefs.reduce((sum, c) => sum + c.width, 0)

  // Live entity notifications: any contract row change refreshes this list.
  useLiveSubscription({ entity: 'contracts' })

  // Sync state to URL
  useEffect(() => {
    const url = new URL(window.location.href)
    const set = (key: string, val: string | undefined) => {
      if (val) url.searchParams.set(key, val)
      else url.searchParams.delete(key)
    }
    set('q', filters.search)
    set('contract_type_uid', filters.contractTypeUid)
    set('status', filters.status)
    set('asset_uid', filters.assetUid)
    set('conformity', filters.conformity)
    set('pii', filters.pii)
    set('individual', filters.individual)
    set('termination_from', filters.terminationFrom)
    set('termination_before', filters.terminationBefore)
    set('sort_by', sort?.by)
    set('sort_dir', sort ? sort.dir : undefined)
    set('page', page > 1 ? String(page) : undefined)
    window.history.replaceState({}, '', url.toString())
  }, [filters, page, sort])

  const { data: contracts = [], total, isLoading, error } = useContracts(org || 'demo', {
    ...filters,
    // The URL carries `individual` as a string; the hook wants a tri-state
    // boolean (undefined = no filter).
    individual: filters.individual === undefined ? undefined : filters.individual === 'true',
    // The Contracts list shows real contracts only. Lightweight types (typed
    // relations displayed as contacts, e.g. syndic/concierge/notaire) are
    // surfaced on the asset Contacts tab, not here. See spec
    // 2026-06-20-carnet-entretien-contacts §8.
    lightweight: false,
    page,
    limit: pageSize,
    // Parties + Actif columns need stakeholders (with role + user) and assets.
    include: ['contract_type', 'stakeholders', 'user', 'assets'],
    // Show only chain-head contracts — a superseded contract (whose code its
    // successor reuses) must not appear as a duplicate row.
    headsOnly: true,
    sortBy: sort?.by,
    sortDir: sort?.dir,
  })

  const { data: contractTypes = [] } = useContractTypes(org || 'demo')

  // Open contract-quality tickets (domain `contract`), indexed by contract UID —
  // drives the Conformité column (spec 2026-07-31-01).
  const { byContract: qualityByContract } = useContractQualityIssues(org || 'demo')
  const { byCode: subtypeCatalog } = useTicketSubtypes(org || 'demo')

  // The conformity filter is client-side: the server doesn't know ticket
  // state, and the badge index covers the whole org.
  const visibleContracts = useMemo(() => {
    if (filters.conformity !== 'nonconform') return contracts
    return contracts.filter((c: any) => (qualityByContract[c.uid] || []).length > 0)
  }, [contracts, filters.conformity, qualityByContract])

  // Helper function to handle multilingual contract titles
  const getContractTitle = (contract: any) => {
    return contract?.name || contract?.code || t('list.untitled')
  }

  // Get contract type name
  const getContractTypeName = (contract: any) => {
    if (!contract?.contract_type) return '-'
    const names = contract.contract_type.names || contract.contract_type.name
    if (names) {
      if (typeof names === 'string') return names
      return names[locale] || names.en || names.fr || Object.values(names)[0] || contract.contract_type.code || '-'
    }
    return contract.contract_type.code || '-'
  }

  // Resolve a contract type's localized display name (used by the Type column filter).
  const typeDisplayName = (type: any) => {
    const names = type?.names || type?.name
    if (!names) return type?.code || '-'
    if (typeof names === 'string') return names
    return names[locale] || names.en || names.fr || Object.values(names)[0] || type?.code || '-'
  }

  const getStatusDot = (status: string) => {
    const option = STATUS_OPTIONS.find(opt => opt.value === status)
    return option?.dot || 'bg-muted-foreground/60'
  }

  // Get status label
  const getStatusLabel = (status: string) => {
    return t(`status.${status}` as any, { defaultValue: status })
  }

  // Set breadcrumbs
  const breadcrumbs = useMemo(() => [
    { title: t('common:nav.home', 'Home'), path: `/${org}` },
    { title: t('list.title') }
  ], [org, t])

  useBreadcrumbs(breadcrumbs)

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value || undefined }))
    setPage(1)
  }

  const handleContractTypeChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      contractTypeUid: value === 'all' ? undefined : value
    }))
    setPage(1)
  }

  const handleStatusChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      status: value === 'all' ? undefined : value
    }))
    setPage(1)
  }

  const handlePIIChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      pii: value === 'all' ? undefined : (value as ContractFilters['pii'])
    }))
    setPage(1)
  }

  const handleIndividualChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      individual: value === 'all' ? undefined : value
    }))
    setPage(1)
  }

  const handleConformityChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      conformity: value === 'all' ? undefined : value
    }))
  }

  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  const applySort = (by: SortField, dir: 'asc' | 'desc') => {
    setSort({ by, dir })
    setPage(1)
  }

  const formatDate = (dateString: string) => {
    // Follow the app locale, not the browser locale — an FR session must
    // render 01/03/2024, not 3/1/2024.
    return new Date(dateString).toLocaleDateString(locale)
  }

  // Localized stakeholder role label (falls back across locale → en → fr → code).
  const getPartyRole = (stakeholder: any): string => {
    const names = stakeholder?.stakeholder_type?.names
    if (names) {
      return names[locale] || names.en || names.fr || Object.values(names)[0] || stakeholder?.stakeholder_type?.code || ''
    }
    return stakeholder?.stakeholder_type?.code || ''
  }

  // Party display name — the linked user's name, else their email.
  const getPartyName = (stakeholder: any): string => {
    return stakeholder?.user?.name || stakeholder?.user?.email || t('detail.user_unknown')
  }

  // A contract is "expired" (périmé) when its end date is in the past and it has
  // not been formally closed — the end_date renders in destructive red.
  const isExpired = (contract: any): boolean => {
    if (!contract?.end_date || contract?.status === 'closed') return false
    const end = new Date(contract.end_date)
    if (Number.isNaN(end.getTime())) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return end < today
  }

  // The tacit-renewal denunciation window is closing: termination_date is set,
  // the contract is still open, and the deadline is within the warning window.
  const isTerminationSoon = (contract: any): boolean => {
    if (!contract?.termination_date || contract?.status === 'closed') return false
    const deadline = new Date(contract.termination_date)
    if (Number.isNaN(deadline.getTime())) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const windowEnd = new Date(today)
    windowEnd.setDate(windowEnd.getDate() + TERMINATION_WARNING_DAYS)
    return deadline >= today && deadline <= windowEnd
  }

  // Row click navigates to the contract detail. Nested links (name, assets)
  // stopPropagation so they don't double-navigate.
  const handleRowClick = (contractUid: string) => {
    navigate({
      to: '/$org/contracts/$contractUid',
      params: { org: org || 'demo', contractUid }
    })
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <PageHeader
          icon={SECTION_ICONS.contracts}
          title={t('list.title')}
          description={t('list.subtitle')}
          actions={
            <>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="contracts-columns-button">
                  <AppIcon path={mdiViewColumn} size="sm" className="mr-2" />
                  {t('list.columns')}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-2">
                {CONTRACT_COLUMNS.filter((c) => !c.required).map((col) => (
                  <label
                    key={col.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      checked={isCol(col.id)}
                      onChange={() => toggleColumn(col.id)}
                      data-testid={`contracts-column-toggle-${col.id}`}
                    />
                    {col.labelNs ? t(col.labelKey, { ns: col.labelNs }) : t(col.labelKey as any)}
                  </label>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1 w-full justify-start"
                  onClick={resetColumns}
                  data-testid="contracts-columns-reset"
                >
                  {t('list.columns_reset')}
                </Button>
              </PopoverContent>
            </Popover>
            <Link to="/$org/contracts/timeline" params={{ org: org || 'demo' }} search={{ contract_type_uid: undefined, status: undefined, q: undefined, asset_uid: undefined }}>
              <Button variant="outline" data-testid="contracts-timeline-link">
                <AppIcon path={mdiChartGantt} size="sm" className="mr-2" />
                {t('timeline.title')}
              </Button>
            </Link>
            <Can perm="contracts.write">
              <Link to="/$org/contracts/create" params={{ org: org || 'demo' }} search={{ asset_uid: undefined }}>
                <Button data-testid="create-contract">
                  <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                  {t('list.create')}
                </Button>
              </Link>
            </Can>
            </>
          }
        />

        {/* Search bar — filters/sort now live in the column headers (Excel-style) */}
        <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:max-w-sm">
          <AppIcon path={mdiMagnify} size="sm" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            placeholder={t('list.search_placeholder')}
            value={filters.search || ''}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        {(filters.terminationFrom || filters.terminationBefore) && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-destructive/30 bg-destructive/5 px-2.5 py-1 text-xs"
            data-testid="contracts-notice-window-chip"
          >
            {t('list.notice_window')}
            <button
              type="button"
              onClick={() => { setFilters({ ...filters, terminationFrom: undefined, terminationBefore: undefined }); setPage(1) }}
              aria-label={t('list.notice_window_clear')}
              title={t('list.notice_window_clear')}
              className="text-muted-foreground hover:text-foreground"
              data-testid="contracts-notice-window-chip-clear"
            >
              <AppIcon path={mdiClose} size="xs" />
            </button>
          </span>
        )}
        </div>

        {/* Contracts List. Issue #1627: the empty list is the page-level panel,
            like Signatures, not a small glyph inside the table card. */}
        {!isLoading && !error && visibleContracts.length === 0 ? (
          <EmptyState
            live={Object.values(filters).some(Boolean)}
            icon={SECTION_ICONS.contracts}
            title={t('list.empty')}
            description={t(Object.values(filters).some(Boolean) ? 'list.empty_filtered_description' : 'list.empty_description')}
            data-testid="contracts-empty"
          />
        ) : (
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-64">
                <p className="text-destructive mb-2">
                  {t('list.error')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {(error as any)?.title || (error as any)?.message || 'Unknown error occurred'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full table-fixed" data-testid="contracts-table">
                  <colgroup>
                    {visibleColumnDefs.map((c) => (
                      <col key={c.id} style={{ width: `${(c.width / widthTotal) * 100}%` }} />
                    ))}
                  </colgroup>
                  <thead className="bg-muted/50">
                    <tr>
                      {/* CONTRAT — sortable */}
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        <Popover>
                          <PopoverTrigger
                            className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors"
                            data-testid="header-contract"
                          >
                            {t('list.column_contract')}
                            {sort?.by === 'name'
                              ? (sort.dir === 'asc' ? <AppIcon path={mdiArrowUp} size="xs" className="text-primary" /> : <AppIcon path={mdiArrowDown} size="xs" className="text-primary" />)
                              : <AppIcon path={mdiArrowUpDown} size="xs" className="opacity-40" />}
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-44 p-1">
                            <PopoverClose asChild>
                              <button type="button" onClick={() => applySort('name', 'asc')} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-left normal-case hover:bg-muted" data-testid="sort-name-asc">
                                <AppIcon path={mdiArrowUp} size="xs" /> {t('list.sort_asc')}
                              </button>
                            </PopoverClose>
                            <PopoverClose asChild>
                              <button type="button" onClick={() => applySort('name', 'desc')} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-left normal-case hover:bg-muted" data-testid="sort-name-desc">
                                <AppIcon path={mdiArrowDown} size="xs" /> {t('list.sort_desc')}
                              </button>
                            </PopoverClose>
                          </PopoverContent>
                        </Popover>
                      </th>

                      {/* TYPE — filterable */}
                      {isCol('type') && (
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        <Popover>
                          <PopoverTrigger
                            className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors"
                            data-testid="header-type"
                          >
                            {t('list.column_type')}
                            <AppIcon path={mdiFilterVariant} size="xs" className={`${filters.contractTypeUid ? 'text-primary' : 'opacity-40'}`} />
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-56 p-1 max-h-72 overflow-auto">
                            <PopoverClose asChild>
                              <button type="button" onClick={() => handleContractTypeChange('all')} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-xs text-left normal-case hover:bg-muted" data-testid="filter-type-all">
                                {t('list.all_contract_types')}
                                {!filters.contractTypeUid && <AppIcon path={mdiCheck} size="xs" />}
                              </button>
                            </PopoverClose>
                            {contractTypes.map((type) => (
                              <PopoverClose asChild key={type.uid}>
                                <button type="button" onClick={() => handleContractTypeChange(type.uid)} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-xs text-left normal-case hover:bg-muted" data-testid={`filter-type-${type.uid}`}>
                                  {typeDisplayName(type)}
                                  {filters.contractTypeUid === type.uid && <AppIcon path={mdiCheck} size="xs" />}
                                </button>
                              </PopoverClose>
                            ))}
                          </PopoverContent>
                        </Popover>
                      </th>
                      )}

                      {/* STATUT — filterable */}
                      {isCol('status') && (
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        <Popover>
                          <PopoverTrigger
                            className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors"
                            data-testid="header-status"
                          >
                            {t('list.column_status')}
                            <AppIcon path={mdiFilterVariant} size="xs" className={`${filters.status ? 'text-primary' : 'opacity-40'}`} />
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-48 p-1">
                            <PopoverClose asChild>
                              <button type="button" onClick={() => handleStatusChange('all')} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-xs text-left normal-case hover:bg-muted" data-testid="filter-status-all">
                                {t('list.all_statuses')}
                                {!filters.status && <AppIcon path={mdiCheck} size="xs" />}
                              </button>
                            </PopoverClose>
                            {STATUS_OPTIONS.map((status) => (
                              <PopoverClose asChild key={status.value}>
                                <button type="button" onClick={() => handleStatusChange(status.value)} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-xs text-left normal-case hover:bg-muted" data-testid={`filter-status-${status.value}`}>
                                  {t(`status.${status.value}` as any)}
                                  {filters.status === status.value && <AppIcon path={mdiCheck} size="xs" />}
                                </button>
                              </PopoverClose>
                            ))}
                          </PopoverContent>
                        </Popover>
                      </th>
                      )}

                      {/* PARTIES */}
                      {/* CONFORMITÉ — filterable (spec 2026-07-31-01) */}
                      {isCol('conformity') && (
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        <Popover>
                          <PopoverTrigger
                            className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors"
                            data-testid="header-conformity"
                          >
                            {t('list.column_conformity')}
                            <AppIcon path={mdiFilterVariant} size="xs" className={`${filters.conformity ? 'text-primary' : 'opacity-40'}`} />
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-56 p-1">
                            <PopoverClose asChild>
                              <button type="button" onClick={() => handleConformityChange('all')} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-xs text-left normal-case hover:bg-muted" data-testid="filter-conformity-all">
                                {t('list.conformity_all')}
                                {!filters.conformity && <AppIcon path={mdiCheck} size="xs" />}
                              </button>
                            </PopoverClose>
                            <PopoverClose asChild>
                              <button type="button" onClick={() => handleConformityChange('nonconform')} className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-xs text-left normal-case hover:bg-muted" data-testid="filter-conformity-nonconform">
                                {t('list.conformity_only_flagged')}
                                {filters.conformity === 'nonconform' && <AppIcon path={mdiCheck} size="xs" />}
                              </button>
                            </PopoverClose>
                          </PopoverContent>
                        </Popover>
                      </th>
                      )}

                      {/* PARTIES — filterable on `individual` (spec 2026-08-13-02 §6).
                          Server-side EXISTS over the stakeholders' party type;
                          deliberately independent of the PII filter below. */}
                      {isCol('parties') && (
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        <Popover>
                          <PopoverTrigger
                            className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors"
                            data-testid="header-parties"
                          >
                            {t('list.column_parties')}
                            <AppIcon path={mdiFilterVariant} size="xs" className={`${filters.individual ? 'text-primary' : 'opacity-40'}`} />
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-56 p-1">
                            {[
                              { value: 'all', labelKey: 'pii.individual.filter_all', testId: 'filter-individual-all' },
                              { value: 'true', labelKey: 'pii.individual.filter_true', testId: 'filter-individual-true' },
                              { value: 'false', labelKey: 'pii.individual.filter_false', testId: 'filter-individual-false' },
                            ].map(option => (
                              <PopoverClose asChild key={option.value}>
                                <button
                                  type="button"
                                  onClick={() => handleIndividualChange(option.value)}
                                  className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-xs text-left normal-case hover:bg-muted"
                                  data-testid={option.testId}
                                >
                                  {t(option.labelKey, { ns: 'common' })}
                                  {(filters.individual ?? 'all') === option.value && <AppIcon path={mdiCheck} size="xs" />}
                                </button>
                              </PopoverClose>
                            ))}
                          </PopoverContent>
                        </Popover>
                      </th>
                      )}

                      {/* DONNÉES PERSONNELLES — filterable (spec 2026-08-13-02 §6) */}
                      {isCol('pii') && (
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        <Popover>
                          <PopoverTrigger
                            className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors"
                            data-testid="header-pii"
                          >
                            {t('pii.filter.label', { ns: 'common' })}
                            <AppIcon path={mdiFilterVariant} size="xs" className={`${filters.pii ? 'text-primary' : 'opacity-40'}`} />
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-56 p-1">
                            {[
                              { value: 'all', labelKey: 'pii.filter.all', testId: 'filter-pii-all' },
                              { value: 'true', labelKey: 'pii.filter.true', testId: 'filter-pii-true' },
                              { value: 'false', labelKey: 'pii.filter.false', testId: 'filter-pii-false' },
                              { value: 'unknown', labelKey: 'pii.filter.unknown', testId: 'filter-pii-unknown' },
                            ].map(option => (
                              <PopoverClose asChild key={option.value}>
                                <button
                                  type="button"
                                  onClick={() => handlePIIChange(option.value)}
                                  className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-xs text-left normal-case hover:bg-muted"
                                  data-testid={option.testId}
                                >
                                  {t(option.labelKey, { ns: 'common' })}
                                  {(filters.pii ?? 'all') === option.value && <AppIcon path={mdiCheck} size="xs" />}
                                </button>
                              </PopoverClose>
                            ))}
                          </PopoverContent>
                        </Popover>
                      </th>
                      )}

                      {/* ACTIF */}
                      {isCol('asset') && (
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider" data-testid="header-asset">
                        {t('list.column_asset')}
                      </th>
                      )}

                      {/* PÉRIODE — sortable */}
                      {isCol('period') && (
                      <th className="px-4 py-3 text-left text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        <Popover>
                          <PopoverTrigger
                            className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground transition-colors"
                            data-testid="header-period"
                          >
                            {t('list.column_period')}
                            {sort?.by === 'start_date'
                              ? (sort.dir === 'asc' ? <AppIcon path={mdiArrowUp} size="xs" className="text-primary" /> : <AppIcon path={mdiArrowDown} size="xs" className="text-primary" />)
                              : <AppIcon path={mdiArrowUpDown} size="xs" className="opacity-40" />}
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-44 p-1">
                            <PopoverClose asChild>
                              <button type="button" onClick={() => applySort('start_date', 'asc')} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-left normal-case hover:bg-muted" data-testid="sort-period-asc">
                                <AppIcon path={mdiArrowUp} size="xs" /> {t('list.sort_asc')}
                              </button>
                            </PopoverClose>
                            <PopoverClose asChild>
                              <button type="button" onClick={() => applySort('start_date', 'desc')} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-left normal-case hover:bg-muted" data-testid="sort-period-desc">
                                <AppIcon path={mdiArrowDown} size="xs" /> {t('list.sort_desc')}
                              </button>
                            </PopoverClose>
                          </PopoverContent>
                        </Popover>
                      </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {visibleContracts.map((contract) => {
                      const stakeholders = ((contract as any).stakeholders || []) as any[]
                      const assets = ((contract as any).assets || []) as any[]
                      const shownParties = stakeholders.slice(0, MAX_PARTY_CHIPS)
                      const extraParties = stakeholders.length - shownParties.length
                      const shownAssets = assets.slice(0, MAX_ASSET_BADGES)
                      const extraAssets = assets.length - shownAssets.length
                      const expired = isExpired(contract)
                      const terminationSoon = isTerminationSoon(contract)
                      return (
                      <tr
                        key={contract.uid}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(contract.uid)}
                        data-testid={`row-contract-${contract.uid}`}
                      >
                        <td className="px-4 py-4">
                          <div>
                            <Link
                              to="/$org/contracts/$contractUid"
                              params={{ org: org || 'demo', contractUid: contract.uid }}
                              className="text-sm font-medium text-foreground hover:text-primary hover:underline transition-colors"
                              data-testid={`contract-title-${contract.uid}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {getContractTitle(contract)}
                            </Link>
                            {contract.code && (
                              <div className="mt-1">
                                <Badge variant="secondary" className="text-xs">
                                  {contract.code}
                                </Badge>
                              </div>
                            )}
                          </div>
                        </td>
                        {isCol('type') && (
                        <td className="px-4 py-4">
                          {(contract as any).contract_type ? (
                            <div className="text-sm text-muted-foreground">
                              {getContractTypeName(contract)}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground/60" data-testid={`contract-type-none-${contract.uid}`}>
                              {t('list.type_none')}
                            </span>
                          )}
                        </td>
                        )}
                        {isCol('status') && (
                        <td className="px-4 py-4">
                          <Badge variant="outline" className="gap-1.5 font-normal" data-testid={`contract-status-${contract.uid}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${getStatusDot(contract.status || 'draft')}`} aria-hidden="true" />
                            {getStatusLabel(contract.status || 'draft')}
                          </Badge>
                        </td>
                        )}
                        {/* CONFORMITÉ */}
                        {isCol('conformity') && (
                        <td className="px-4 py-4" data-testid={`contract-conformity-${contract.uid}`}>
                          {(qualityByContract[contract.uid] || []).length > 0 ? (
                            <Link
                              to="/$org/steering"
                              params={{ org: org || 'demo' }}
                              search={{ domain: 'contract', q: contract.code || undefined } as any}
                              onClick={(e) => e.stopPropagation()}
                              title={(qualityByContract[contract.uid] || [])
                                .map((i) => subtypeLabel(subtypeCatalog[i.subtype || ''], locale) || i.title)
                                .join(' · ')}
                              data-testid={`contract-conformity-link-${contract.uid}`}
                            >
                              <Badge variant="outline" className="gap-1 text-xs font-normal text-warning border-warning/40 hover:bg-muted">
                                <AppIcon path={mdiShieldAlertOutline} size="xs" className="shrink-0" />
                                {t('list.conformity_flagged', { count: (qualityByContract[contract.uid] || []).length })}
                              </Badge>
                            </Link>
                          ) : (
                            <span className="text-sm text-muted-foreground/60">—</span>
                          )}
                        </td>
                        )}
                        {/* PARTIES */}
                        {isCol('parties') && (
                        <td className="px-4 py-4" data-testid={`contract-parties-${contract.uid}`}>
                          {shownParties.length > 0 ? (
                            <div className="flex flex-nowrap items-center gap-1 overflow-hidden">
                              {shownParties.map((s) => (
                                <Badge key={s.uid} variant="secondary" className="text-xs font-normal min-w-0 max-w-full truncate block">
                                  {getPartyRole(s) ? `${getPartyRole(s)} : ${getPartyName(s)}` : getPartyName(s)}
                                </Badge>
                              ))}
                              {extraParties > 0 && (
                                <Badge variant="outline" className="text-xs font-normal">
                                  {t('list.plus_n', { n: extraParties })}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground/60">—</span>
                          )}
                          {/* "Individual" marker on the parties cell, so the
                              filtered column explains itself (spec §8). */}
                          {(contract as any).with_individual && (
                            <div className="mt-1">
                              <Badge
                                variant="outline"
                                className="text-xs font-normal gap-1"
                                title={t('pii.individual.hint', { ns: 'common' })}
                                data-testid={`contract-individual-${contract.uid}`}
                              >
                                <AppIcon path={mdiAccount} size="xs" />
                                {t('pii.individual.badge', { ns: 'common' })}
                              </Badge>
                            </div>
                          )}
                        </td>
                        )}
                        {/* DONNÉES PERSONNELLES — read-only badge; the verdict
                            control lives on the contract detail page. */}
                        {isCol('pii') && (
                        <td className="px-4 py-4" data-testid={`contract-pii-${contract.uid}`}>
                          <PIIBadge flags={(contract as any).flags} hideWhenUnassessed testId={`contract-pii-badge-${contract.uid}`} />
                        </td>
                        )}
                        {/* ACTIF */}
                        {isCol('asset') && (
                        <td className="px-4 py-4" data-testid={`contract-assets-${contract.uid}`}>
                          {shownAssets.length > 0 ? (
                            <div className="flex flex-nowrap items-center gap-1 overflow-hidden">
                              {shownAssets.map((ca) => (
                                <Link
                                  key={ca.uid}
                                  className="min-w-0"
                                  to="/$org/assets/$assetUid"
                                  params={{ org: org || 'demo', assetUid: ca.asset_uid }}
                                  search={{ tab: 'contracts' }}
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`contract-asset-link-${contract.uid}-${ca.asset_uid}`}
                                >
                                  <Badge variant="outline" className="gap-1 text-xs font-normal min-w-0 max-w-full truncate hover:bg-muted">
                                    <AppIcon path={SECTION_ICONS.assets} size="xs" className="shrink-0" />
                                    {(ca.asset as any)?.name || t('detail.asset_unknown')}
                                  </Badge>
                                </Link>
                              ))}
                              {extraAssets > 0 && (
                                <Badge variant="outline" className="text-xs font-normal">
                                  {t('list.plus_n', { n: extraAssets })}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground/60">—</span>
                          )}
                        </td>
                        )}
                        {/* PÉRIODE */}
                        {isCol('period') && (
                        <td className="px-4 py-4">
                          <div className="text-sm text-muted-foreground">
                            {contract.start_date && (
                              <div className="flex items-center">
                                <AppIcon path={mdiCalendar} size="xs" className="mr-1" />
                                {formatDate(contract.start_date)}
                              </div>
                            )}
                            {contract.end_date && (
                              <div
                                className={`text-xs ${expired ? 'text-destructive font-medium' : ''}`}
                                data-testid={`contract-end-date-${contract.uid}`}
                              >
                                {t('list.period_to')} {formatDate(contract.end_date)}
                              </div>
                            )}
                            {terminationSoon && (contract as any).termination_date && (
                              <div
                                className="text-xs text-warning font-medium mt-0.5"
                                data-testid={`contract-termination-soon-${contract.uid}`}
                              >
                                {t('list.termination_by', { date: formatDate((contract as any).termination_date) })}
                              </div>
                            )}
                          </div>
                        </td>
                        )}
                      </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Pagination */}
        {total > pageSize && (
          <div className="flex justify-center space-x-2">
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              {t('list.page_previous')}
            </Button>
            <span className="px-4 py-2 text-sm text-muted-foreground" data-testid="contracts-page-label">
              {t('list.page_of', { page, total: pageCount })}
            </span>
            <Button
              variant="outline"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= pageCount}
            >
              {t('list.page_next')}
            </Button>
          </div>
        )}
    </div>
  )
}